/**
 * controller.js — RC 1.53 Phase 4.1
 *
 * OperationLifecycleController skeleton. Module-level singleton; per-
 * tab; per-(job, action) operation map. No business logic yet — this
 * is the coordination primitive that adapters will plug into.
 *
 * Public API:
 *
 *   dispatch({ jobId, actionCode, runId?, idempotencyKey?, run }) → Promise
 *     Enqueues an operation. The `run` callback is the adapter's actual
 *     work (typically returning the polling helper's Promise). If an
 *     operation for (jobId, actionCode) is already in-flight, dispatch
 *     is a NO-OP and returns the in-flight Promise (no double-charge
 *     mitigation — Risk #1).
 *
 *   abort(jobId, actionCode, reason)
 *     Marks the operation as aborted and emits operation:abort. Does
 *     NOT cancel the underlying polling Promise — adapters own that
 *     concern in Phase 4.2+.
 *
 *   getOperation(jobId, actionCode) → { status, runId, ... } | null
 *
 *   listOperations(jobId) → Array
 *
 *   isAnyRunning(jobId) → boolean
 *     True when ANY action for the job is in `dispatching` / `polling`.
 *
 *   reset()  — test-only
 *
 * The controller does NOT touch:
 *   • the four polling helpers (used verbatim via adapters)
 *   • idempotency keys (passed through)
 *   • cache invalidation (adapters will do this in 4.2)
 *   • URL state (adapters will do this in 4.2)
 *   • billing primitives (NEVER touched by the controller)
 *
 * Phase 4.1 scope: skeleton + events + map only. Adapters in
 * /adapters/ are stub interfaces — they will be filled in 4.2.
 */

import { emit, OPERATION_EVENT_TYPES } from './operationEvents.js';

export const OPERATION_STATUS = Object.freeze({
  IDLE:        'idle',
  DISPATCHING: 'dispatching',
  POLLING:     'polling',
  // Phase -1 Tier 1 — S4 — re-attach to an in-flight backend op
  // (read-only). RECOVERING is NOT a chargeable state and MUST NOT
  // disable user-initiated Generate buttons. See
  // OPERATION_LIFECYCLE_AUDIT.md Part 1 cause 1 + Phase -1
  // doc §3 for why this is a separate state from POLLING.
  RECOVERING:  'recovering',
  // Phase -1 Tier 1 — S4 — transient network blip between polls.
  // Permit is still reserved; the marker is preserved (mirrors the
  // RC 1.60.P0E transient-failure marker-preservation contract,
  // generalised across all action codes).
  RECOVERABLE_STALL: 'recoverable_stall',
  // RC 1.53 Phase 4.2.1.1 — `stabilizing` is the transient state
  // between "polling helper returned a terminal envelope" and "the
  // section has finished ALL post-success work" (finalize-charge,
  // hydration verifier, credits refresh, cache invalidation, URL
  // write, success toast, cooldown). The activity indicator stays
  // mounted throughout this phase so the operator sees one
  // continuous "we're still working" signal across the entire
  // generate→charge→reconcile→bind sequence.
  //
  // The controller enters STABILIZING automatically when the
  // adapter's `run` callback resolves. The section MUST call
  // `markComplete(jobId, actionCode, envelope)` after every
  // stabilization step has landed; only then does the op
  // transition to COMPLETE and the activity indicator unmount.
  STABILIZING: 'stabilizing',
  COMPLETE:    'complete',
  FAILED:      'failed',
  ABORTED:     'aborted',
});

const _ACTIVE_STATUSES = new Set([
  OPERATION_STATUS.DISPATCHING,
  OPERATION_STATUS.POLLING,
  // Phase -1 Tier 1 — S4 — RECOVERING + RECOVERABLE_STALL count as
  // active so concurrent dispatches are rejected and the workspace's
  // global busy state reflects "something is happening on the
  // backend for this (target, action)".
  OPERATION_STATUS.RECOVERING,
  OPERATION_STATUS.RECOVERABLE_STALL,
  // STABILIZING is treated as active so concurrent dispatches are
  // still rejected and `isAnyRunning(jobId)` keeps the workspace's
  // global busy state truthy through the post-success chain.
  OPERATION_STATUS.STABILIZING,
]);

/** Per-tab singleton state — keyed `${jobId}::${actionCode}`. */
const _operations = new Map();

function _key(jobId, actionCode) {
  return `${jobId || ''}::${actionCode || ''}`;
}

function _validateActionCode(code) {
  const ALLOWED = new Set([
    'cv_variant',
    'cover_letter',
    'interview_prep',
    'analyse_fit',
  ]);
  if (!ALLOWED.has(code)) {
    throw new Error(
      `OperationLifecycleController: unknown actionCode '${code}'. `
      + `Allowed: ${[...ALLOWED].join(', ')}.`,
    );
  }
}

/**
 * Dispatch a new operation. Returns the Promise from `run` (or, if an
 * in-flight operation already exists for the (jobId, actionCode), the
 * existing in-flight Promise) so the caller can `await` terminal
 * resolution without re-dispatching the underlying work.
 */
export function dispatch({
  jobId,
  actionCode,
  runId = null,
  idempotencyKey = null,
  run,
}) {
  if (!jobId)     throw new Error('dispatch: jobId is required');
  if (!actionCode) throw new Error('dispatch: actionCode is required');
  if (typeof run !== 'function') {
    throw new Error('dispatch: run must be a function returning a Promise');
  }
  _validateActionCode(actionCode);

  const k = _key(jobId, actionCode);
  const existing = _operations.get(k);

  // Risk #1 mitigation — no double-dispatch while in-flight.
  if (existing && _ACTIVE_STATUSES.has(existing.status)) {
    return existing.promise;
  }

  const promise = (async () => {
    try {
      // Adapter's run returns the terminal canonical envelope (or
      // throws on failure). The controller transitions through
      // DISPATCHING → POLLING → COMPLETE/FAILED based on adapter
      // callbacks. Phase 4.1 wires the simplest contract: the run
      // function returns an envelope and the controller treats that
      // as terminal-complete.
      _patch(k, { status: OPERATION_STATUS.DISPATCHING });
      // Yield one microtask so the synchronous _operations.set(...)
      // below has landed before any adapter callbacks (onRunIdReady,
      // onProgress, onPolling) attempt to patch the operation map.
      // Without this, an adapter that fires callbacks synchronously
      // during its first turn would patch a missing key.
      await Promise.resolve();
      const envelope = await run({
        onPolling: () => _patch(k, { status: OPERATION_STATUS.POLLING }),
        onProgress: (status, progressLabel) =>
          emit(OPERATION_EVENT_TYPES.PROGRESS, {
            jobId, actionCode,
            runId: _operations.get(k)?.runId || null,
            status, progressLabel: progressLabel || null,
          }),
        onRunIdReady: (newRunId) => {
          _patch(k, { runId: newRunId });
        },
      });
      _patch(k, {
        status: OPERATION_STATUS.STABILIZING,
        terminalEnvelope: envelope,
      });
      // RC 1.53 Phase 4.2.1.1 — emit a dedicated `stabilizing` event
      // so subscribers (activity indicator, devtools trace) can render
      // post-success copy. The legacy `operation:complete` event is
      // deferred until the section explicitly calls `markComplete`.
      emit(OPERATION_EVENT_TYPES.STABILIZING, {
        jobId, actionCode,
        runId: _operations.get(k)?.runId || null,
        envelope,
      });
      return envelope;
    } catch (err) {
      _patch(k, {
        status: OPERATION_STATUS.FAILED,
        error: err,
      });
      emit(OPERATION_EVENT_TYPES.FAIL, {
        jobId, actionCode,
        runId: _operations.get(k)?.runId || null,
        error: { message: err?.message, code: err?.code, status: err?.status },
      });
      throw err;
    }
  })();

  _operations.set(k, {
    jobId,
    actionCode,
    runId,
    idempotencyKey,
    status: OPERATION_STATUS.DISPATCHING,
    startedAt: Date.now(),
    promise,
    error: null,
    terminalEnvelope: null,
  });

  emit(OPERATION_EVENT_TYPES.START, {
    jobId, actionCode, runId, idempotencyKey,
  });

  return promise;
}

function _patch(k, fields) {
  const cur = _operations.get(k);
  if (!cur) return;
  _operations.set(k, { ...cur, ...fields });
}

/**
 * Mount-recovery API — register a known in-flight operation that this
 * controller did not dispatch (e.g. the section read an in-flight
 * sessionStorage marker on mount and is reattaching to an existing
 * backend run). Phase 4.1 just records the shape; 4.2 will wire the
 * polling resumption.
 */
export function attachExistingRun({
  jobId,
  actionCode,
  runId,
  idempotencyKey = null,
  run,
}) {
  if (!jobId)     throw new Error('attachExistingRun: jobId is required');
  if (!actionCode) throw new Error('attachExistingRun: actionCode is required');
  if (!runId)     throw new Error('attachExistingRun: runId is required');
  if (typeof run !== 'function') {
    throw new Error('attachExistingRun: run must be a function');
  }
  _validateActionCode(actionCode);

  const k = _key(jobId, actionCode);
  const existing = _operations.get(k);
  if (existing && _ACTIVE_STATUSES.has(existing.status)) {
    // Already attached / dispatched — no-op (mirror dispatch rule).
    return existing.promise;
  }
  return dispatch({ jobId, actionCode, runId, idempotencyKey, run });
}

export function abort(jobId, actionCode, reason = 'user_aborted') {
  const k = _key(jobId, actionCode);
  const op = _operations.get(k);
  if (!op || !_ACTIVE_STATUSES.has(op.status)) return false;
  _patch(k, { status: OPERATION_STATUS.ABORTED, error: { message: reason } });
  emit(OPERATION_EVENT_TYPES.ABORT, {
    jobId, actionCode, runId: op.runId, reason,
  });
  return true;
}

/**
 * RC 1.53 Phase 4.2.1.1 — section-driven terminal transition.
 *
 * After the controller's `run` callback resolves, the operation is
 * placed into `STABILIZING`. The owning section must call
 * `markComplete(jobId, actionCode, envelope?)` once all post-success
 * work has landed: finalize-charge, hydration verifier, credits
 * refresh, cache invalidation, URL/state writes, success toast.
 *
 * Only at that point does the `operation:complete` event fire and
 * the activity indicator unmount. This guarantees the orchestrator
 * is "first visible, last gone".
 *
 * Idempotent — safe to call multiple times; subsequent invocations
 * are no-ops.
 */
export function markComplete(jobId, actionCode, envelope = null) {
  const k = _key(jobId, actionCode);
  const op = _operations.get(k);
  if (!op) return false;
  if (op.status === OPERATION_STATUS.COMPLETE) return false;
  // Only valid transitions are STABILIZING → COMPLETE. We tolerate
  // POLLING→COMPLETE for adapters that bypass the stabilizing
  // contract (defence-in-depth; not used in Phase 4.2.1.1).
  if (
    op.status !== OPERATION_STATUS.STABILIZING
    && op.status !== OPERATION_STATUS.POLLING
    && op.status !== OPERATION_STATUS.DISPATCHING
  ) return false;
  _patch(k, {
    status: OPERATION_STATUS.COMPLETE,
    terminalEnvelope: envelope || op.terminalEnvelope || null,
  });
  emit(OPERATION_EVENT_TYPES.COMPLETE, {
    jobId, actionCode,
    runId: _operations.get(k)?.runId || null,
    envelope: envelope || op.terminalEnvelope || null,
  });
  return true;
}

/**
 * RC 1.53 Phase 4.2.1.1 — section-driven failure transition.
 *
 * Section catches an error AFTER the run callback resolved (e.g.
 * finalize-charge 5xx, hydration verifier raise) and surfaces it
 * to the controller so the activity indicator unmounts.
 *
 * Idempotent.
 */
export function markFailed(jobId, actionCode, err) {
  const k = _key(jobId, actionCode);
  const op = _operations.get(k);
  if (!op) return false;
  // Idempotent: only active operations may transition to FAILED.
  // Already-terminal ops (COMPLETE / FAILED / ABORTED) are no-ops so
  // a defensive `finally` block can safely call this without
  // clobbering a successful markComplete.
  if (!_ACTIVE_STATUSES.has(op.status)) return false;
  _patch(k, {
    status: OPERATION_STATUS.FAILED,
    error: err || { message: 'failed' },
  });
  emit(OPERATION_EVENT_TYPES.FAIL, {
    jobId, actionCode,
    runId: _operations.get(k)?.runId || null,
    error: err
      ? { message: err.message, code: err.code, status: err.status }
      : { message: 'failed' },
  });
  return true;
}

export function getOperation(jobId, actionCode) {
  const op = _operations.get(_key(jobId, actionCode));
  if (!op) return null;
  // Strip the internal Promise from the snapshot — callers should
  // not be able to mutate or re-attach to a hot Promise outside the
  // controller's own lifecycle.
  const { promise: _p, ...snapshot } = op;
  void _p;
  return snapshot;
}

export function listOperations(jobId) {
  const out = [];
  for (const [k, op] of _operations) {
    if (!jobId || k.startsWith(`${jobId}::`)) {
      const { promise: _p, ...snapshot } = op;
      void _p;
      out.push(snapshot);
    }
  }
  return out;
}

export function isAnyRunning(jobId) {
  for (const op of listOperations(jobId)) {
    if (_ACTIVE_STATUSES.has(op.status)) return true;
  }
  return false;
}

/** Test-only — clear the operation map. */
export function _resetForTests() {
  _operations.clear();
}
